
import React from 'react';
import { SimulationConfig } from '@/types';
import { SmartInput } from './SmartInput';
import { CollapsibleSection } from './CollapsibleSection';
import { Cpu, Info } from 'lucide-react';
import { InfoPopup } from './InfoPopup';

interface AdvancedPhysicsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const AdvancedPhysicsSection: React.FC<AdvancedPhysicsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
    return (
        <CollapsibleSection 
            title="Advanced Physics" 
            icon={Cpu} 
            darkMode={config.darkMode} 
            settingKey="physics_advanced"
            isLocked={isLocked}
            lockRequiredPlan={minPlan as any}
            onUpgradeClick={onUpgradeClick}
        >
            <div className="flex items-center gap-2 mb-2 px-4 mt-4">
                <Cpu className="w-3.5 h-3.5 text-cyan-500 opacity-40" />
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-500">Integrator Tuning</h4>
                <InfoPopup settingKey="physicsAdvancedTuning" darkMode={config.darkMode} />
            </div>
            <div className={`flex flex-col gap-4 p-4 mt-2 mb-4 rounded-3xl border ${config.darkMode ? 'bg-cyan-500/5 border-cyan-500/10' : 'bg-white border-zinc-100 shadow-inner'}`}>
                <SmartInput 
                    label="Substeps" 
                    min={1} max={20} step={1}
                    value={config.physicsSubsteps} 
                    onChange={v => setConfig(p => ({...p, physicsSubsteps: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="physicsSubsteps"
                />
                <SmartInput 
                    label="Air Resistance" 
                    min={0} max={1.0} step={0.001} 
                    value={config.airResistance} 
                    onChange={v => setConfig(p => ({...p, airResistance: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="airResistance"
                />
                <SmartInput 
                    label="Max Velocity" 
                    min={1} max={100} step={0.1}
                    value={config.maxVelocityLimit} 
                    onChange={v => setConfig(p => ({...p, maxVelocityLimit: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="maxVelocityLimit"
                />
                <SmartInput 
                    label="Min Velocity" 
                    min={0} max={50} step={0.1}
                    value={config.minVelocityLimit} 
                    onChange={v => setConfig(p => ({...p, minVelocityLimit: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="minVelocityLimit"
                />
                <SmartInput 
                    label="Anti-Stall Force" 
                    min={0} max={10} step={0.1}
                    value={config.antiStallMultiplier} 
                    onChange={v => setConfig(p => ({...p, antiStallMultiplier: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="antiStallMultiplier"
                />
            </div>
        </CollapsibleSection>
    );
};
